import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import HotelBudgetForecastTableDayCell from 'lumen/components/hotel/budget-forecast/table/day-cell';
import type { TableLayoutSettings } from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import HotelBudgetForecastTableTdWithVariance from 'lumen/components/hotel/budget-forecast/table/td-with-variance';
import SettingsBudgetForecastTableBudgetForecastTdName from 'lumen/components/settings/budget-forecast/table-budget-forecast/td-name';
import SettingsBudgetForecastTableBudgetForecastTdNumber from 'lumen/components/settings/budget-forecast/table-budget-forecast/td-number';
import SettingsBudgetForecastTableBudgetForecastThActions from 'lumen/components/settings/budget-forecast/table-budget-forecast/th-actions';
import type {
  BudgetForecastDataItem,
  BudgetForecastEntry,
  Day,
} from 'lumen/models';
import { groupItemsBySegmentKey } from 'lumen/services/budget-forecast';
import { calculateVariance } from 'lumen/utils/budget-forecast';
import type { CapacityWithMeta } from 'lumen/utils/budget-forecast/data-fetching';
import {
  ADDITIONAL_DATA_TYPES,
  COLUMN_PROPERTIES,
  type DataType,
} from 'lumen/utils/constants/budget-forecast-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import safeDivide from 'lumen/utils/numeric/safe-divide';

import styles from './table-budget-forecast.module.css';

interface BudgetForecastRow extends CustomEmberTableRow {
  [COLUMN_PROPERTIES.NAME]: {
    isUnmapped: boolean;
    value: string;
  };
  children: BudgetForecastRow[];
}

function calculateTotal(items: BudgetForecastDataItem[]): {
  adr: number;
  revenue: number;
  rooms: number;
} {
  const total = items.reduce(
    (acc, item) => ({
      rooms: acc.rooms + item.roomsToShow,
      revenue: acc.revenue + item.revenueToShow,
    }),
    { rooms: 0, revenue: 0 },
  );

  return {
    rooms: total.rooms,
    revenue: total.revenue,
    adr: safeDivide(total.revenue, total.rooms) ?? 0,
  };
}

interface SettingsBudgetForecastTableBudgetForecastSignature {
  Args: {
    capacities: CapacityWithMeta | null | undefined;
    data: BudgetForecastDataItem[];
    entry: BudgetForecastEntry | null;
    expandedRowIds: string[];
    isDisabled: boolean;
    isLatestVersionSelected: boolean;
    isLoading: boolean;
    isSegmentVisibilityEditButtonDisabled: boolean;
    layoutSettings: TableLayoutSettings;
    onCollapseAllRows: () => void;
    onRowCollapseToggle: (rowId: string) => void;
    openSegmentVisibilityModal: () => void;
    variance: Variance;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastTableBudgetForecast extends Component<SettingsBudgetForecastTableBudgetForecastSignature> {
  @service declare budgetForecast: Services['budget-forecast'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get columns(): CustomEmberTableColumn[] {
    const baseColumns = [
      {
        name: '',
        valuePath: '',
        isFixed: 'left' as const,
        hasRightBorder: true,
        subcolumns: [
          {
            name: '',
            valuePath: COLUMN_PROPERTIES.NAME,
            hasRightBorder: true,
            width: 250,
            thComponent: SettingsBudgetForecastTableBudgetForecastThActions,
            tdComponent: SettingsBudgetForecastTableBudgetForecastTdName,
            isEditDisabled:
              this.args.isLoading ||
              this.args.isSegmentVisibilityEditButtonDisabled,
            editDisabledTooltipMessage: this.disabledSegmentVisibilityMessage,
            canShowEditButton:
              this.args.entry &&
              this.args.entry.canEdit &&
              this.args.isLatestVersionSelected,
            onEdit: (): void => {
              this.args.openSegmentVisibilityModal();
            },
            isCollapseDisabled: this.args.expandedRowIds.length === 0,
            collapseRows: (): void => {
              this.args.onCollapseAllRows();
            },
          },
        ],
      },
      {
        name: this.intl.t('settings.budget_forecast.total'),
        valuePath: '',
        isFixed: 'left' as const,
        hasRightBorder: true,
        subcolumns: this.getSubcolumns({
          hasRightBorder: true,
          isEditable: false,
          valuePathPrefix: COLUMN_PROPERTIES.TOTAL,
        }),
      },
    ];

    const uniqueItemsByDate = [
      ...new Map(
        this.args.data.map((item) => [item.date.format('YYYY-MM-DD'), item]),
      ).values(),
    ].sort((a, b) => a.date.diff(b.date));

    const canEdit =
      !!this.args.entry?.canEdit && this.args.isLatestVersionSelected;

    const additionalColumns = uniqueItemsByDate.map((item, i) => ({
      name: item.formattedDate,
      valuePath: '',
      hasRightBorder: i !== uniqueItemsByDate.length - 1,
      thComponent: HotelBudgetForecastTableDayCell,
      value: item.formattedDate,
      day: this.store.peekRecord<Day>('day', item.date.format('YYYY-MM-DD')),
      classNames: 'justify-center',
      subcolumns: this.getSubcolumns({
        hasRightBorder: i !== uniqueItemsByDate.length - 1,
        isEditable: canEdit,
        valuePathPrefix: item.date.format('YYYY-MM-DD'),
      }),
    }));

    return [...baseColumns, ...additionalColumns];
  }

  get dataGroupedByDataType(): Partial<
    Record<DataType, BudgetForecastDataItem[]>
  > {
    return this.args.data.reduce<
      Partial<Record<DataType, BudgetForecastDataItem[]>>
    >((acc, item) => {
      const { dataType } = item;

      if (!acc[dataType]) {
        acc[dataType] = [];
      }

      acc[dataType].push(item);

      return acc;
    }, {});
  }

  get disabledSegmentVisibilityMessage(): string | undefined {
    if (this.args.isLoading) {
      return undefined;
    }

    if (this.args.entry?.isLocked) {
      return this.args.view === 'budget'
        ? this.intl.t('budget_forecast.budget.locked_message')
        : this.intl.t('budget_forecast.forecast.locked_message');
    }

    if (this.args.entry?.isArchived) {
      return this.args.view === 'budget'
        ? this.intl.t('budget_forecast.budget.archived_disabled_message')
        : this.intl.t('budget_forecast.forecast.archived_disabled_message');
    }

    return this.args.view === 'budget'
      ? this.intl.t('budget_forecast.budget.segment_visibility_disabled')
      : this.intl.t('budget_forecast.forecast.segment_visibility_disabled');
  }

  get rows(): BudgetForecastRow[] {
    const { dataGroupedByDataType } = this;
    const { layoutSettings } = this.args;
    const availableDataTypes = [
      ...Object.keys(dataGroupedByDataType),
      ...ADDITIONAL_DATA_TYPES,
    ];
    const dataTypes = availableDataTypes.filter(
      (dataType) =>
        !layoutSettings.hiddenDataTypes.includes(dataType as DataType),
    ) as DataType[];

    const rows = this.sortDataTypes(dataTypes, this.rowsOrder)
      .map((dataType) => {
        if (dataType === 'capacity') {
          return this.getCapacityRow();
        }

        return this.getRowForItemsOfDataType(
          dataGroupedByDataType[dataType] ?? [],
        );
      })
      .filter((row) => row !== null);

    if (rows[1]) {
      rows[1]['className'] = 'border-t-2 border-neutral-emphasis border-solid';
    }

    return rows;
  }

  get rowsOrder(): DataType[] {
    const { layoutSettings, view } = this.args;

    return [view, ...(layoutSettings.dataTypesOrder ?? [])];
  }

  getAvailableInventoryForItem(item: BudgetForecastDataItem): number | null {
    const { capacities } = this.args.capacities || {};

    if (!item.isRoot || item.dataType !== this.args.view || !capacities) {
      return null;
    }

    const capacityForDate = capacities.find((capacity) =>
      capacity.date.isSame(item.date, 'day'),
    );

    if (!capacityForDate) {
      return null;
    }

    return capacityForDate.availableRooms;
  }

  getCapacityRow(): BudgetForecastRow | null {
    if (!this.args.capacities) {
      return null;
    }

    const { capacities, meta } = this.args.capacities;

    const row = {
      [COLUMN_PROPERTIES.NAME]: {
        value: this.intl.t('settings.budget_forecast.inventory_excl_ooo'),
        isUnmapped: false,
      },
      isCollapsed: false,
      isEditable: false,
      isDisabled: false,
      id: 'capacity',
      children: [],
      className: '',
    } as BudgetForecastRow;

    row[COLUMN_PROPERTIES.TOTAL] = {
      [COLUMN_PROPERTIES.ROOMS]: {
        value: meta.totals.available_rooms,
      },
      [COLUMN_PROPERTIES.ADR]: {
        value: null,
      },
      [COLUMN_PROPERTIES.REVENUE]: {
        value: null,
      },
    };

    capacities.forEach((capacity) => {
      row[capacity.date.format('YYYY-MM-DD')] = {
        [COLUMN_PROPERTIES.ROOMS]: {
          value: capacity.availableRooms,
        },
        [COLUMN_PROPERTIES.ADR]: {
          value: null,
        },
        [COLUMN_PROPERTIES.REVENUE]: {
          value: null,
        },
      };
    });

    return row;
  }

  getRowForItem(
    item: BudgetForecastDataItem,
    itemsBySegmentKey: Record<string, BudgetForecastDataItem[]>,
  ): BudgetForecastRow | null {
    const { view, isDisabled, variance, capacities } = this.args;
    const items = itemsBySegmentKey[item.segmentKey] ?? [];

    if (items.every((i) => i.isItemHidden)) {
      return null;
    }

    const children = item.filteredChildren
      .map((child) => this.getRowForItem(child, itemsBySegmentKey))
      .filter((child) => child !== null);

    const rowId = item.isRoot
      ? item.dataType
      : `${item.dataType}-${item.segmentKey}`;

    let className = '';

    if (item.dataType === view && item.isUnmapped) {
      className = styles['table-row--unmapped'];
    } else if (item.dataType === view && item.isItemForHiddenSegment) {
      className = styles['table-row--disabled'];
    }

    const row: BudgetForecastRow = {
      [COLUMN_PROPERTIES.NAME]: {
        value: item.formattedSegment,
        isUnmapped: item.isUnmapped,
      },
      isCollapsed: !this.args.expandedRowIds.includes(rowId),
      isEditable: item.dataType === view,
      isDisabled: isDisabled || this.isRowDisabled(items),
      id: rowId,
      children,
      className,
    };

    const total = calculateTotal(items);
    const baseItemsInSegment = this.args.data.filter(
      (dataItem) =>
        dataItem.dataType === view && dataItem.segmentKey === item.segmentKey,
    );
    const baseItemsTotal = {
      ...calculateTotal(baseItemsInSegment),
      dataType: view,
    };
    const totalVariances = calculateVariance(baseItemsTotal, total, variance);
    const totalAvailableInventory =
      capacities?.meta.totals.available_rooms ?? 0;

    const varianceType = item.dataType === view ? VARIANCES.NONE : variance;

    row[COLUMN_PROPERTIES.TOTAL] = {
      [COLUMN_PROPERTIES.ROOMS]: {
        value: total.rooms,
        varianceType,
        varianceValue: totalVariances[COLUMN_PROPERTIES.ROOMS],
        hasWarning:
          item.dataType === view && item.isRoot
            ? total.rooms - totalAvailableInventory > 0
            : false,
        inventoryDifference: total.rooms - totalAvailableInventory,
      },
      [COLUMN_PROPERTIES.ADR]: {
        value: total.adr,
        varianceType,
        varianceValue: totalVariances[COLUMN_PROPERTIES.ADR],
      },
      [COLUMN_PROPERTIES.REVENUE]: {
        value: total.revenue,
        varianceType,
        varianceValue: totalVariances[COLUMN_PROPERTIES.REVENUE],
      },
    };

    items.forEach((dataItem) => {
      const isItemDisabled = this.isItemDisabled(dataItem);
      const variances = calculateVariance(
        dataItem.belongsTo(view).value(),
        dataItem,
        variance,
      );

      const hasUnsavedChanges =
        dataItem.hasUnsavedChanges ||
        dataItem.hasUndistributedChanges ||
        dataItem.hasChildrenWithUnsavedChanges ||
        dataItem.hasChildrenWithUndistributedChanges;

      row[dataItem.date.format('YYYY-MM-DD')] = {
        [COLUMN_PROPERTIES.ROOMS]: {
          availableInventory: this.getAvailableInventoryForItem(dataItem),
          hasUnsavedChanges,
          isDisabled: isItemDisabled,
          onChange: (value: number): void => {
            dataItem.rooms = value;
            this.trackValueChange(dataItem, 'rms');
          },
          value: dataItem.roundedRooms,
          varianceType,
          varianceValue: variances[COLUMN_PROPERTIES.ROOMS],
        },
        [COLUMN_PROPERTIES.ADR]: {
          hasUnsavedChanges,
          isDisabled: isItemDisabled,
          onChange: (value: number): void => {
            dataItem.adr = value;
            this.trackValueChange(dataItem, 'adr');
          },
          value: dataItem.roundedAdr,
          varianceType,
          varianceValue: variances[COLUMN_PROPERTIES.ADR],
        },
        [COLUMN_PROPERTIES.REVENUE]: {
          value: dataItem.roundedRevenue,
          varianceType,
          varianceValue: variances[COLUMN_PROPERTIES.REVENUE],
        },
      };
    });

    return row;
  }

  getRowForItemsOfDataType(
    items: BudgetForecastDataItem[],
  ): BudgetForecastRow | null {
    const rootItem = items.find((item) => item.isRoot);

    if (!rootItem) {
      return null;
    }

    const itemsBySegmentKey = groupItemsBySegmentKey(items);
    const row = this.getRowForItem(rootItem, itemsBySegmentKey);

    if (!row) {
      return null;
    }

    return row;
  }

  getSubcolumns(options: {
    hasRightBorder: boolean;
    isEditable: boolean;
    valuePathPrefix: string;
  }): {
    hasRightBorder?: boolean;
    name: string;
    tdComponent:
      | typeof HotelBudgetForecastTableTdWithVariance
      | typeof SettingsBudgetForecastTableBudgetForecastTdNumber;
    textAlign: 'right';
    valuePath: string;
  }[] {
    const { hasRightBorder, isEditable, valuePathPrefix } = options;

    return [
      {
        name: this.intl.t('settings.budget_forecast.rooms.short'),
        tdComponent: isEditable
          ? SettingsBudgetForecastTableBudgetForecastTdNumber
          : HotelBudgetForecastTableTdWithVariance,
        textAlign: 'right' as const,
        valuePath: `${valuePathPrefix}.${COLUMN_PROPERTIES.ROOMS}`,
      },
      {
        name: this.intl.t('settings.budget_forecast.adr.short'),
        tdComponent: isEditable
          ? SettingsBudgetForecastTableBudgetForecastTdNumber
          : HotelBudgetForecastTableTdWithVariance,
        textAlign: 'right' as const,
        valuePath: `${valuePathPrefix}.${COLUMN_PROPERTIES.ADR}`,
      },
      {
        hasRightBorder,
        name: this.intl.t('settings.budget_forecast.revenue.short'),
        tdComponent: HotelBudgetForecastTableTdWithVariance,
        textAlign: 'right' as const,
        valuePath: `${valuePathPrefix}.${COLUMN_PROPERTIES.REVENUE}`,
      },
    ];
  }

  isItemDisabled(item: BudgetForecastDataItem): boolean {
    // If there are no items to be force distributed, we disable on a row level.
    if (this.budgetForecast.itemsToBeForceDistributed.length === 0) {
      return false;
    }

    const itemsToBeForceDistributedForDate =
      this.budgetForecast.itemsToBeForceDistributed.filter((i) =>
        i.date.isSame(item.date, 'day'),
      );

    // If there are no items to be force distributed for this date, disable the whole column.
    if (itemsToBeForceDistributedForDate.length === 0) {
      return true;
    }

    return itemsToBeForceDistributedForDate.every((i) => i.id !== item.id);
  }

  /**
   * Utility function to check if inputs of a row should be disabled.
   */
  isRowDisabled(items: BudgetForecastDataItem[]): boolean {
    // If there are items to be force distributed, we want disable on a cell/item level.
    if (this.budgetForecast.itemsToBeForceDistributed.length !== 0) {
      return false;
    }

    return items.some(
      (item) =>
        item.hasParentWithUndistributedChanges ||
        item.hasChildrenWithUndistributedChanges,
    );
  }

  @action
  onRowCollapseToggle(row: CustomEmberTableRow): void {
    const { id } = row;

    if (typeof id !== 'string') {
      return;
    }

    this.trackExpandRow(id);
    this.args.onRowCollapseToggle(id);
  }

  @action
  sortDataTypes(dataTypes: DataType[], sortBy: DataType[]): DataType[] {
    const orderMap = new Map();
    sortBy.forEach((item, index) => {
      orderMap.set(item, index);
    });

    return dataTypes.sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const indexA = orderMap.has(a) ? orderMap.get(a) : Infinity;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const indexB = orderMap.has(b) ? orderMap.get(b) : Infinity;

      return indexA - indexB;
    });
  }

  @action
  trackExpandRow(rowId: string): void {
    if (this.args.expandedRowIds.includes(rowId)) {
      this.usageAnalytics.track('Rows folded', {
        eventTrigger: 'segment-row-toggled',
        segmentName: rowId,
      });
      return;
    }

    this.usageAnalytics.track('Rows unfolded', {
      eventTrigger: 'segment-row-toggled',
      segmentName: rowId,
    });
  }

  @action
  trackValueChange(
    item: BudgetForecastDataItem,
    changedProperty: 'rms' | 'adr',
  ): void {
    this.usageAnalytics.track('Value Edited', {
      eventTrigger: 'cell-edited',
      changedProperty,
      segmentName: item.formattedSegment,
      level: item.depth,
    });
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @isLoading={{@isLoading}}
      @onRowCollapseToggle={{this.onRowCollapseToggle}}
      @rows={{this.rows}}
      class={{styles.table}}
      data-test-id="table-budget-forecast"
    />
  </template>
}
